import { z, type ZodType } from 'zod';
import {
  ChatMessageSchema,
  RequestChatReadBodySchema,
  RequestChatUnreadSummaryResponseSchema,
  type ChatMessage,
  type RequestChatReadBody,
  type RequestChatUnreadSummaryResponse,
} from '@magimp/shared';
import { request } from '../client.js';
import { withQuery } from './_query.js';

const ChatListResponseSchema = z
  .object({
    items: z.array(ChatMessageSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type ChatListResponse = z.infer<typeof ChatListResponseSchema>;

export function listChat(
  id: string,
  query: { cursor?: string; limit?: number } = {},
): Promise<ChatListResponse> {
  const params = new URLSearchParams();
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return request<ChatListResponse>(
    withQuery(`/requests/${encodeURIComponent(id)}/chat`, qs),
    { method: 'GET' },
    {},
    ChatListResponseSchema as unknown as ZodType<ChatListResponse>,
  );
}

export function postChat(id: string, body: string): Promise<ChatMessage> {
  return request<ChatMessage>(
    `/requests/${encodeURIComponent(id)}/chat`,
    {
      method: 'POST',
      body: JSON.stringify({ body }),
      headers: { 'content-type': 'application/json' },
    },
    {},
    ChatMessageSchema as unknown as ZodType<ChatMessage>,
  );
}

// -- chat read-state (#570) -------------------------------------------

// WHY (#570): the unread-summary endpoint accepts up to 200 `requestId`
// query params. Caller passes the full set; the server's strict zod
// rejects > 200 with VALIDATION_ERROR. URLSearchParams.append handles
// the repeated-key shape (`?requestId=LR-00001&requestId=LR-00002...`)
// that the controller expects.
export function fetchRequestChatUnreadSummary(
  requestIds: readonly string[],
  opts: { signal?: AbortSignal } = {},
): Promise<RequestChatUnreadSummaryResponse> {
  const params = new URLSearchParams();
  for (const id of requestIds) params.append('requestId', id);
  return request<RequestChatUnreadSummaryResponse>(
    withQuery('/requests/chat-unread-summary', params.toString()),
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    RequestChatUnreadSummaryResponseSchema as unknown as ZodType<RequestChatUnreadSummaryResponse>,
  );
}

export function markChatRead(id: string, body: RequestChatReadBody): Promise<void> {
  return request<void>(
    `/requests/${encodeURIComponent(id)}/chat/read`,
    {
      method: 'POST',
      body: JSON.stringify(RequestChatReadBodySchema.parse(body)),
      headers: { 'content-type': 'application/json' },
    },
    {},
    z.void(),
  );
}

export function markChatUnread(id: string): Promise<void> {
  return request<void>(
    `/requests/${encodeURIComponent(id)}/chat/mark-unread`,
    { method: 'POST' },
    {},
    z.void(),
  );
}

export function unmarkChatUnread(id: string): Promise<void> {
  return request<void>(
    `/requests/${encodeURIComponent(id)}/chat/unmark-unread`,
    { method: 'POST' },
    {},
    z.void(),
  );
}
